import "solid-devtools";
import { setElementInterface } from "solid-devtools/setup";
import { elementInterface } from "@lightningtv/solid/devtools";

setElementInterface(elementInterface);

import { createRenderer, Config, loadFonts } from "@lightningtv/solid";
import { WebGlCoreRenderer, SdfTextRenderer } from "@lightningjs/renderer/webgl";
import { CanvasCoreRenderer, CanvasTextRenderer } from "@lightningjs/renderer/canvas";

import { Inspector } from "@lightningjs/renderer/inspector";
import { HashRouter, FocusStackProvider, KeepAliveRoute, lazy } from "@lightningtv/solid/primitives";
import { Route, Navigate } from "@solidjs/router";
import App from "./pages/App";
import Browse from "./pages/Browse";
import TMDB from "./pages/TMDB";

import { tmdbData, destroyData } from "./api/tmdbData";
import NotFound from "./pages/NotFound";
import fonts from "./fonts";
import { browsePreload } from "./api/browsePreload";
import { detailPreload } from "./api/detailPreload";
import LeftNavWrapper from "./pages/LeftNavWrapper";
import theme from "theme";

const Player = lazy(() => import("./pages/Player"));
const DetailPage = lazy(() => import("./pages/Detail"));
const LoginPage = lazy(() => import("./pages/Login"));

let numImageWorkers = 4;
const urlParams = new URLSearchParams(window.location.search);
const numWorkers = urlParams.get("numImageWorkers");
const screenSize = urlParams.get("size") || "default";
const rendererMode = urlParams.get("mode") || "webgl";
const animationsEnabled = urlParams.get("animate") || "true";

if (numWorkers) {
  numImageWorkers = parseInt(numWorkers);
}

const deviceLogicalPixelRatio = {
  "720": 0.666667,
  medium: 0.8,
  "1080": 1,
  "4k": 2,
  default: window.innerHeight / 1080
}[screenSize];

const logFps = true;
Config.debug = false;
// Config.keyDebug = true;
Config.animationsEnabled = animationsEnabled === "true";
Config.fontSettings.fontFamily = "Roboto";
Config.fontSettings.color = theme.textPrimary;
Config.fontSettings.fontSize = 32;
Config.domRendererEnabled = false;
(Config as any).simpleAnimationsEnabled = true && !Config.domRendererEnabled;
// Config.focusDebug = true;

Config.rendererOptions = {
  fpsUpdateInterval: logFps ? 300 : 0,
  inspector: import.meta.env.DEV ? Inspector : undefined,
  textureMemory: {
    criticalThreshold: 200e6,
    targetThresholdLevel: 0.8
  },
  numImageWorkers, // temp fix for renderer bug
  // Set the resolution based on window height
  // 720p = 0.666667, 1080p = 1, 1440p = 1.5, 2160p = 2
  deviceLogicalPixelRatio,
  devicePhysicalPixelRatio: window.devicePixelRatio || 1,
  createImageBitmapSupport: "auto",
  boundsMargin: 100,
  targetFPS: 0,
  enableClear: false
};

// Ideally you'd do two separate builds for canvas and webgl to reduce bundle size.
if (rendererMode === "canvas") {
  Config.rendererOptions.fontEngines = [CanvasTextRenderer as any];
  Config.rendererOptions.renderEngine = CanvasCoreRenderer;
} else {
  Config.rendererOptions.fontEngines = [SdfTextRenderer];
  Config.rendererOptions.renderEngine = WebGlCoreRenderer;
}

const { renderer, render } = createRenderer();
loadFonts(fonts);
// Prepare for RC3 of Renderer
import {
  Rounded,
  RoundedWithShadow,
  RoundedWithBorder,
  RoundedWithBorderAndShadow,
  RadialGradient,
  LinearGradient,
  HolePunch
} from "@lightningjs/renderer/webgl/shaders";
const shManager = renderer.stage.shManager;
shManager.registerShaderType("rounded", Rounded);
shManager.registerShaderType("roundedWithBorder", RoundedWithBorder);
shManager.registerShaderType("roundedWithShadow", RoundedWithShadow);
shManager.registerShaderType("roundedWithBorderWithShadow", RoundedWithBorderAndShadow);
shManager.registerShaderType("radialGradient", RadialGradient);
shManager.registerShaderType("linearGradient", LinearGradient);
shManager.registerShaderType("holePunch", HolePunch);
render(() => (
  <FocusStackProvider>
    <HashRouter root={(props) => <App {...props} />}>
      <Route path="" component={LeftNavWrapper}>
        <Route path="" component={() => <Navigate href="/browse/all" />} />
        <Route path="tmdb" component={TMDB} preload={tmdbData} />
        {/* <Route path="browse/:filter" component={KeepAliveWrapper(Browse)} preload={browsePreload} /> */}
        <KeepAliveRoute id="browse" path="browse/:filter" component={Browse} preload={browsePreload} />

        <Route path="login" component={LoginPage} />

        <Route path="detail/:type/:id" component={DetailPage} preload={detailPreload} />
        <Route path="*all" component={NotFound} />
      </Route>
      <Route path="player">
        <Route path=":id" component={Player} />
      </Route>
    </HashRouter>
  </FocusStackProvider>
));
